import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Switch,
  Snackbar,
  Alert,
} from '@mui/material';
import { PageContainer } from 'components/shared';
import { useGetSettingsQuery, useUpdateSettingsMutation } from 'state/api';

// Who the notifications are aimed at. This POS/inventory admin deals with
// customers and suppliers — not marketplace vendors/delivery-men.
const DEFAULT_AUDIENCE = 'Users';
const AUDIENCE_OPTIONS = ['Users', 'Customers', 'Suppliers'];

// The fixed catalogue of notification events this store can send, grouped into
// tabs. This is structural configuration (like the status tabs on the Orders
// page), not fetched data: it defines which toggles exist. The `message` values
// are editable defaults — the admin's edits are persisted to the settings API.
const MESSAGE_TABS = [
  {
    label: 'Order',
    items: [
      { title: 'Order Pending Message', message: 'Your order has been placed and is pending confirmation.' },
      { title: 'Order Confirmed Message', message: 'Your order has been confirmed.' },
      { title: 'Order Processing Message', message: 'Your order is being processed.' },
      { title: 'Order Shipped Message', message: 'Your order has been shipped.' },
      { title: 'Order Delivered Message', message: 'Your order has been delivered.' },
      { title: 'Order Cancelled Message', message: 'Your order has been cancelled.' },
      { title: 'Order Refunded Message', message: 'A refund has been issued for your order.' },
    ],
  },
  {
    label: 'Refund',
    items: [
      { title: 'Refund Requested Message', message: 'We have received your refund request.' },
      { title: 'Refund Approved Message', message: 'Your refund request has been approved.' },
      { title: 'Refund Rejected Message', message: 'Your refund request has been rejected.' },
      { title: 'Refund Completed Message', message: 'Your refund has been processed successfully.' },
    ],
  },
  {
    label: 'Others',
    items: [
      { title: 'Payment Received Message', message: 'Your payment has been received successfully.' },
      { title: 'Invoice Ready Message', message: 'Your invoice is ready to view and download.' },
      { title: 'Promotional Offer Message', message: 'Check out our latest offers and discounts.' },
    ],
  },
];

// Build a fully-populated status/message map (every toggle off, every message at
// its default). Used by Reset so the persisted state is deterministic rather
// than relying on the API's deep-merge to clear individual keys.
const buildDefaults = () => {
  const status = {};
  const messages = {};
  MESSAGE_TABS.forEach((tab, tabIndex) => {
    status[tabIndex] = {};
    messages[tabIndex] = {};
    tab.items.forEach((item, index) => {
      status[tabIndex][index] = false;
      messages[tabIndex][index] = item.message;
    });
  });
  return { status, messages };
};

const NotificationConfiguration = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [notificationStatus, setNotificationStatus] = useState({});
  const [messages, setMessages] = useState({});
  const [selectedUser, setSelectedUser] = useState(DEFAULT_AUDIENCE);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [pendingStatus, setPendingStatus] = useState(null); // Store pending toggle change
  const [toast, setToast] = useState(null);

  const { data: settingsResp } = useGetSettingsQuery();
  const [updateSettings, { isLoading: saving }] = useUpdateSettingsMutation();

  // Load saved push-notification config once settings arrive.
  useEffect(() => {
    const saved = settingsResp?.data?.custom?.pushNotifications;
    if (!saved) return;
    if (saved.status) setNotificationStatus(saved.status);
    if (saved.audience) setSelectedUser(saved.audience);
    if (saved.messages) setMessages(saved.messages);
  }, [settingsResp]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleMessageChange = (tabIndex, index, value) => {
    setMessages((prev) => ({
      ...prev,
      [tabIndex]: { ...prev[tabIndex], [index]: value },
    }));
  };

  const handleStatusChange = (index, tabIndex) => {
    const currentStatus = notificationStatus[tabIndex]?.[index] || false;
    const newStatus = !currentStatus;

    // Store the pending change instead of updating state immediately.
    setPendingStatus({ index, tabIndex, newStatus });
    handleDialogOpen(newStatus ? 'Turn On' : 'Turn Off');
  };

  const handleDialogOpen = (action) => {
    setDialogMessage(
      `Want to ${action} this push notification? If you turn it on, users will receive notifications on their devices.`
    );
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    // Cancel action - do not update state.
    setPendingStatus(null);
    setOpenDialog(false);
  };

  const handleDialogConfirm = () => {
    if (pendingStatus) {
      const { index, tabIndex, newStatus } = pendingStatus;
      setNotificationStatus((prevStatus) => ({
        ...prevStatus,
        [tabIndex]: {
          ...prevStatus[tabIndex],
          [index]: newStatus,
        },
      }));
    }
    setPendingStatus(null);
    setOpenDialog(false);
  };

  const persist = async ({ status, audience, msgs, successMsg }) => {
    try {
      await updateSettings({
        custom: {
          pushNotifications: { status, audience, messages: msgs },
        },
      }).unwrap();
      setToast({ severity: 'success', msg: successMsg });
    } catch (err) {
      setToast({ severity: 'error', msg: err?.data?.error?.message || 'Failed to save' });
    }
  };

  const handleSave = () =>
    persist({
      status: notificationStatus,
      audience: selectedUser,
      msgs: messages,
      successMsg: 'Push notification settings saved',
    });

  const handleReset = async () => {
    const { status, messages: defaultMessages } = buildDefaults();
    setNotificationStatus(status);
    setMessages(defaultMessages);
    setSelectedUser(DEFAULT_AUDIENCE);
    await persist({
      status,
      audience: DEFAULT_AUDIENCE,
      msgs: defaultMessages,
      successMsg: 'Push notification settings reset',
    });
  };

  const renderMessages = (items, tabIndex) =>
    items.map((item, index) => (
      <Grid item xs={12} md={4} key={item.title}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="body2">{item.title}</Typography>
          <Switch
            checked={notificationStatus[tabIndex]?.[index] || false}
            onChange={() => handleStatusChange(index, tabIndex)}
          />
        </Box>
        <TextField
          id={`message-${tabIndex}-${index}`}
          label="Message"
          variant="outlined"
          fullWidth
          value={messages[tabIndex]?.[index] ?? item.message}
          onChange={(e) => handleMessageChange(tabIndex, index, e.target.value)}
          multiline
          rows={2}
        />
      </Grid>
    ));

  return (
    <PageContainer
      title="Push Notification Setup"
      subtitle="Configure the automated push notifications your users receive"
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="user-select-label">For</InputLabel>
            <Select
              labelId="user-select-label"
              id="user-select"
              label="For"
              value={selectedUser}
              onChange={(event) => setSelectedUser(event.target.value)}
            >
              {AUDIENCE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Tabs value={selectedTab} onChange={handleTabChange} aria-label="Notification Tabs">
        {MESSAGE_TABS.map((tab) => (
          <Tab key={tab.label} label={tab.label} />
        ))}
      </Tabs>

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2}>
        {renderMessages(MESSAGE_TABS[selectedTab].items, selectedTab)}
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button variant="outlined" color="error" onClick={handleReset} disabled={saving}>
          Reset
        </Button>
        <Button
          variant="contained"
          color="primary"
          sx={{ ml: 2 }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : null}
      </Snackbar>

      <Dialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle>Notification Status</DialogTitle>
        <DialogContent>
          <DialogContentText>{dialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleDialogConfirm} autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default NotificationConfiguration;
